import { BaseDocumentFields, UID } from '../general-models';
import { NewgenusTimestamp } from '../shared-models/timestamp';
import { Timestamp } from 'firebase/firestore';
import { Widget } from './dashboard.models';
import { YearCycle } from './app.models';

export interface OrganizationMeta {
    orgParts: OrgPart[];
}

export interface Organization {
    dashboards?: OrgDashboard;
    description: string;
    editors: string[];
    key: string;
    modifiedTimeStamp: Timestamp;
    name: string;
    orgDateCycles: OrgDateCycle[];
    orgFeatures: { featuresKeys: string[] } & { [key: string]: OrgFeature };
    orgHats: { hatsKeys: string[] } & { [key: string]: OrgHat };
    orgSecurityGroups: { securityGroupsKeys: string[] } & { [key: string]: OrgSecurityGroup };
    orgParts: OrgPart[];
    orgPolicies: { policiesKeys: string[] } & { [key: string]: OrgPolicy };
    orgUsers: { userKeys: string[] } & { [key: string]: OrgUser };
    owners: string[];
    plan?: string;
    settings: OrganizationSettings;
    users: string[];
    targets: {
        [key: string]: TargetGroup;
    };
    apis?: Array<{
        apiDocKey: string;
        name: string;
        type: string;
    }>;
}

/**
 * The order and orientation of org parts determines the hierarchy of the organization, including inheritance of features and permissions that have security groups whose access is inheritable (@see OrgSecurityGroup).
 */
export interface OrgPart extends BaseDocumentFields {
    children?: any[];
    colour: {
        background: string;
        color: string;
        linkColor: string;
    };
    depth: number;
    name: string;
    mainTitle?: string;
    subTitle?: string;
    id: number;
    parentPartKey: string;
    parentRef: string;
    partKey: string;
    partType?: string;
    path: string;

    /** Valuable Final Product - a description of what this position produces. */
    vfp?: string;

    /**
     * When `true`, the VFP for this part is pinned to always be visible on the
     * read-only org board (instead of the default hover-to-reveal behaviour).
     *
     * Toggled per-VFP from edit mode via the pin icon next to each VFP block.
     * Defaults to `undefined` (treated as `false` — hover-only).
     */
    vfpAlwaysVisible?: boolean;

    /**
     * Optional role within the parent container's header strip.
     *
     * When set on a `post`, the post is rendered inside its parent container's
     * (executive / division / department / section / unit) header band — alongside
     * the container name — instead of in the regular body list of children.
     *
     * - `head`     — primary leader of the container (max one per container, soft-enforced in UI).
     * - `deputy`   — second-in-command / acting head when the head is unavailable.
     * - `assistant`— supports the head/deputy; multiple allowed.
     *
     * `undefined` (or absent) means the post is a regular member rendered in the body.
     * Sort order in the header strip is `head → deputy → assistant`, then by `id`.
     *
     * Only meaningful for `partType === 'post'`. Ignored on container parts.
     */
    headerRole?: 'head' | 'deputy' | 'assistant';

    /**
     * Marks this post as belonging to a DETACHED post group — a named subset of
     * the parent container's posts that renders in its own box elsewhere on the
     * board (e.g. the Board of Directors box beside the CEO) instead of in the
     * container's header band or body list.
     *
     * The post still BELONGS to its parent container in every functional sense
     * (staffing, hats, permissions, HFA, drop resolution). Only its on-screen
     * placement changes — drops resolve to the post via the same
     * `droppable-part` fallback used by header-strip chips, so assigning a user
     * to a detached chip is identical to assigning them inside the container.
     *
     * The value is the group key (currently only `'board'`). Generalised as a
     * string so a future second pulled-out box needs no schema change.
     * Only meaningful for `partType === 'post'`. Ignored on container parts.
     */
    detachedGroup?: string;

    /**
     * When `true`, this post does NOT act as a "Held From Above" source — its
     * occupant is not cascaded down onto descendant positions that have no
     * occupant of their own. Use for posts that govern/oversee but do not
     * operationally run the org (e.g. a Board-of-Directors member). The admin
     * decides per post: participating (default, unset/false) vs. board-only
     * (true). Only meaningful for `partType === 'post'`.
     */
    excludeFromHfa?: boolean;

    /**
     * A LINKED post: the `partKey` of another post whose holders this post shows
     * (e.g. Department 19's "Office of the CFO" shows whoever holds the top-row
     * Chief Financial Officer post). It has no people, hat or permissions of its
     * own — assign on the target. If the target is vacant, this post shows the
     * target's held-from-above. No chains: a linked target is ignored.
     * Only meaningful for `partType === 'post'`.
     */
    linkedPartKey?: string;

    /**
     * For a detached group: the `partKey` of the container this group's box
     * should draw a COSMETIC connector line to (e.g. the Executive Division /
     * "Division 7"). Purely decorative — it is NOT a tree edge and creates no
     * parent/child link, HFA, or structural meaning. Set on the group's posts
     * (first non-empty value wins when several posts share a group).
     */
    detachedGroupConnectsTo?: string;

    /**
     * @deprecated - Not sure this is being used.
     */
    parent?: string;
}

export interface OrgFeature {
    name: string;
    description?: string;
    enabled: boolean;
    featureGroups: string[];
    planGroups: string[];
    permissions: string[];
    /** Optional descriptions for each permission key */
    permissionDescriptions?: { [permissionKey: string]: string };
    key: string;
    systemFeature: boolean;
}

export interface OrganizationSettings {
    consolidateHours: boolean;
    dayOfMonthEnd: number;
    dayOfMonthStart: number;
    dayOfWeekEnd: number;
    dayOfWeekStart: number;
    hourOfDayEnd: number;
    hourOfDayStart: number;
    monthWorkingDays: number;
    weekWorkingDays: number;
    workingHours: number;

    /**
     * IANA zone of the country the organisation trades in (e.g. "Africa/Johannesburg"). Working
     * hours, days and months are counted on this clock. Absent = South Africa (`orgTimeZone`).
     */
    timeZone?: string;

    /** When true, hat assignments require approval before security access is granted. Default: false */
    requireHatAssignmentApproval?: boolean;

    // === Entity Color Theme ===
    /** Custom colors for organization entity types (icons, chips, badges) */
    entityColors?: OrgEntityColors;

    // === Notification Settings (Organization Defaults) ===
    /** Default notification settings for policies */
    policyNotifications?: OrgPolicyNotificationSettings;
}

/**
 * Custom color assignments for each organization entity type.
 * Used for icons, badges, chips, and visual identification across the dashboard.
 */
export interface OrgEntityColors {
    users?: string; // default '#1976d2' (blue)
    hats?: string; // default '#7b1fa2' (purple)
    positions?: string; // default '#388e3c' (green)
    securityGroups?: string; // default '#f57c00' (orange)
    policies?: string; // default '#00897b' (teal)
    features?: string; // default '#d32f2f' (red)
    permissions?: string; // default '#5c6bc0' (indigo)
}

/**
 * Organization-level default notification settings for policies
 * Can be overridden per-policy
 */
export interface OrgPolicyNotificationSettings {
    /** Days before acknowledgment deadline to send reminders (e.g., [7, 3, 1]) */
    reminderDaysBefore?: number[];
    /** Send daily reminders after deadline has passed */
    sendOverdueReminders?: boolean;
    /** Maximum number of overdue reminders to send (0 = unlimited) */
    maxOverdueReminders?: number;
    /** Days between overdue reminders (default: 1 = daily) */
    overdueReminderIntervalDays?: number;
    /** Send notification when policy is published */
    notifyOnPublish?: boolean;
    /** Send notification when policy is revoked */
    notifyOnRevoke?: boolean;
}

/**
 * Per-policy reminder settings (overrides organization defaults)
 */
export interface PolicyReminderSettings {
    /** Override: Days before deadline to send reminders */
    reminderDaysBefore?: number[];
    /** Override: Send overdue reminders */
    sendOverdueReminders?: boolean;
    /** Override: Max overdue reminders */
    maxOverdueReminders?: number;
    /** Override: Days between overdue reminders */
    overdueReminderIntervalDays?: number;
    /** Disable all reminders for this policy */
    disableReminders?: boolean;
    /** Override: Send notification when THIS policy is published */
    notifyOnPublish?: boolean;
    /** Override: Send notification when THIS policy is revoked */
    notifyOnRevoke?: boolean;
}

/** Default reminder settings (used when org/policy don't specify) */
export const DEFAULT_POLICY_REMINDER_SETTINGS: Required<OrgPolicyNotificationSettings> = {
    reminderDaysBefore: [7, 3, 1],
    sendOverdueReminders: true,
    maxOverdueReminders: 5,
    overdueReminderIntervalDays: 1,
    notifyOnPublish: true,
    notifyOnRevoke: true,
};

/**
 * System-wide configuration stored in Firestore: systemConfig/policyDefaults
 * These values override hardcoded defaults and can be changed without redeployment
 *
 * MILLISECOND VALUES:
 * - Production: Use days in ms (e.g., 7 days = 604800000, 1 day = 86400000)
 * - Testing: Use seconds in ms (e.g., 30 seconds = 30000)
 *
 * Common conversions:
 * - 1 second = 1000 ms
 * - 1 minute = 60000 ms
 * - 1 hour = 3600000 ms
 * - 1 day = 86400000 ms
 * - 7 days = 604800000 ms
 */
export interface SystemPolicyDefaults {
    /** Milliseconds before deadline to send reminders. Production: [604800000, 259200000, 86400000] (7d, 3d, 1d). Testing: [30000, 15000] (30s, 15s) */
    reminderMsBefore: number[];
    /** Whether to send reminders after deadline passes */
    sendOverdueReminders: boolean;
    /** Maximum number of overdue reminders to send */
    maxOverdueReminders: number;
    /** Milliseconds between overdue reminders. Production: 86400000 (1 day). Testing: 30000 (30 seconds) */
    overdueReminderIntervalMs: number;
    /** Whether to notify users when policy is published */
    notifyOnPublish: boolean;
    /** Whether to notify users when policy is revoked */
    notifyOnRevoke: boolean;
    /** Last modified timestamp */
    modifiedOn?: unknown; // Timestamp
    /** User who last modified */
    modifiedBy?: string;
}

export interface OrgUser {
    api: Record<string, TargetGroup>;
    dashboards: OrgDashboard;
    email?: string;
    featureDetails: OrgUserFeatureDetails;
    features: string[];
    hats: string[];
    hatsDetails: OrgUserSecurityGroupDetails;
    key: string;
    name: string; // Duplicates?? vv
    roles: string[];
    selectedGatewayKey: string;
    statistics: { [configKey: string]: { configName: string; configRef: string; configType: string; repNo: string } };
    targets: { [configKey: string]: { dailyTarget: number; monthlyTarget: number; weeklyTarget: number } };
    uid: string;
    userName: string; // ^^ Duplicates??
    /** Full-size profile photo URL in Firebase Storage */
    photoURL?: string;
    /** Base64 encoded thumbnail image (150x150) for quick loading */
    photoThumbnail?: string;
    /** Hex color for avatar placeholder when no photo is set */
    profileColor?: string;
    /**
     * Lifecycle marker. `'pending'` = wizard pre-staged this user but they
     * have not accepted the invitation yet; render a "Pending Acceptance"
     * pill in any UI and treat them as non-members for permission purposes
     * (they will not appear in `orgUsers.userKeys`, so the permission
     * pipeline already excludes them). `'active'` = accepted org member.
     * Older docs without this field should be treated as `'active'`.
     */
    status?: 'pending' | 'active';
}

export interface OrgDashboard {
    [key: string]: {
        dashboardKey: string;
        description: string;
        name: string;
        widgets: Widget<any>[];
    };
}

interface OrgUserSecurityGroupDetails {
    [key: string]: OrgSecurityGroup;
}

interface OrgUserFeatureDetails {
    [key: string]: {
        permissions: string[];
    };
}

interface OrgSecurityGroupFeatureDetails {
    [key: string]: {
        key: string;
        name: string;
        description?: string;
        permissions: string[];
        enabled: boolean;
        featureGroups: string[];
        planGroups: string[];
    };
}

/**
 * Security groups have features and permissions assigned to them.
 * Security groups can be assigned to users or hats.
 * Users/Hats can have multiple security groups assigned to them.
 * Security groups can or cannot have their access inherited by parent OrgParts (@see OrgPart).
 */
export interface OrgSecurityGroup extends BaseDocumentFields {
    /**
     * Flag to indicate if the security group is assigned to a user.
     */
    assigned: boolean;

    /**
     * The user key that the security group is assigned to.
     */
    assignedTo: string;

    /** Optional description of the security group's purpose */
    description?: string;

    featureDetails: OrgSecurityGroupFeatureDetails;
    features: string[];
    name: string;

    orgPosition: OrgPart; // TODO: remove this, it should come from the hat or the user.
    policies: string[]; // TODO: Remove this
    roles?: string[]; // TODO: Remove this
    multiUsers?: boolean; // TODO: Remove this - they're all technically multi-users
}

export interface Positions extends BaseDocumentFields {
    colour: {
        background: string;
        color: string;
        linkColor: string;
    };
    depth: number;
    id: number;
    key: string;
    name: string;
    parentPartKey: string;
    parentRef: string;
    partKey: string;
    path: string;
}

/**
 * Org hats have security groups assigned to them.
 * These security groups have features and permissions assigned to them.
 * Users are assigned to hats, this is what gives them access to the features and permissions (In some RARE cases, security groups with are assigned directly to users).
 */
export interface OrgHat extends BaseDocumentFields {
    /**
     * The name of the hat.
     */
    name: string;

    /**
     * Keys of the users who are wearing this hat.
     * @deprecated Use assignedToArray instead - this field is not consistently used
     */
    userKeys?: string[];

    /**
     * Array of user UIDs assigned to this hat.
     * This is the canonical source of truth for user assignments.
     * Backend processHats watches this field for changes.
     */
    assignedToArray?: string[];

    /**
     * Array of user UIDs pending approval for this hat.
     * Users in this array are awaiting approval before being moved to assignedToArray.
     * The backend security pipeline ignores this field — only assignedToArray triggers processing.
     */
    pendingAssignedToArray?: string[];

    /**
     * Array of position/part keys this hat is linked to.
     * Used alongside the positions object array.
     */
    positionArray?: string[];

    /**
     * Policies associated with this hat.
     */
    policyKeys: Record<string, string>;

    /**
     * TODO this isn't anything final, it's a place-marker for now.
     * @deprecated - Not finished designing yet...
     */
    checklist?: Array<{
        name: string;
        items: Array<any>;
    }>;

    /**
     * Security groups associated with this hat.
     * @deprecated - ?? not sure which is being used ??
     */
    securityGroups: OrgSecurityGroup[];
    /**
     * Security groups for Firestore array-contains query compatibility.
     * New code saves plain string keys; legacy data may contain OrgSecurityGroup objects.
     * Always normalize entries: `typeof entry === 'string' ? entry : entry.key`
     */
    securityGroupsArray: (string | OrgSecurityGroup)[];
    /**
     * Org Positions/parts associated with this hat.
     *
     */
    positions: Positions[];

    /**
     * Key of the hat.
     */
    key: string;

    /**
     * Description of the hat.
     */
    description: string;

    /**
     * The date the hat was created.
     */
    createdDate: Date;

    /**
     * The date the hat was last modified.
     * @deprecated - this field is replaced by the fields in BaseDocumentFields.
     * @see BaseDocumentFields
     */
    lastUpdatedDate: Date;

    /**
     * If the hat can be worn by multiple users.
     */
    isMultiUsers: boolean;

    /**
     * If the hat is soft deleted.
     */
    isSoftDeleted: boolean;

    /**
     * Explicitly marks this hat as a Special Rights hat.
     * Special Rights hats are not linked to a position — they exist
     * purely to grant additional security-group permissions to users.
     * When true, the hat will NOT be flagged as orphaned/abandoned by
     * cleanup scripts even if it has no positions.
     *
     * Invariant: `positionArray`, `positions`, and `orgPosition` must
     * remain empty. Special-rights hats are assigned to users via
     * `assignedToArray` only — never linked to a post. Writing a partKey
     * into any of those position fields creates a fingerprint collision
     * with that post's own auto-generated post-hat and trips the
     * Duplicate Hats data-integrity check.
     */
    isSpecialRights?: boolean;

    /**
     * Org Parts (Org Positions) associated with this hat.
     * A hat can appear on multiple parts of the organization, this does not affect the hierarchy of feature, permissions and inheritance (@see OrgPart).
     */
    orgPosition: OrgPart;
    /**
     * Indicates if the hat has multiple users or not allowed.
     */
    multiUsers?: boolean;
    featureDetails: OrgSecurityGroupFeatureDetails;
    features: string[];
    users?: any[];
}

export interface OrgTarget {
    targets: Record<string, TargetGroup>;
    type: TargetType;
    key: string;
}

export interface TargetGroup {
    dailyTarget: number;
    monthlyTarget: number;
    weeklyTarget: number;
}

export interface OrgDateCycle {
    businessDaySelection: string;
    default: boolean;
    profileName: string;
    softDeleted: boolean;
    type: string;
    year: string;
    yearCycle: Array<YearCycle>;

    // CRUD fields:
    key: string;
    createdOn: Date;
    createdBy: string;
    lastUpdatedOn: Date;
    lastUpdatedBy: string;
    lastUpdatedName?: string;

    updateHistory: Array<{
        on: Date;
        uid: string;
        update?: string;
        by?: string;
    }>;

    /**
     * A flag to indicate if this date cycle should be marked for review by a user on the frontend.
     * This is required if there are unusual changes to the date cycle, such as a change in the month start / end dates where business days are involved.
     */
    reviewRequired?: boolean;

    /**
     * Version hash to indicate what version of the date cycle this is.
     * This is used to determine if the date cycle has been updated and if a banner/indicator must be shown that the data is updating.
     */
    hash: string;

    /**
     * The updated version of the date cycle that has not yet been published.
     * This is used as a proxy update until the Client ABC has processed the new updated date cycle.
     * Once that is done, the proxy version will be set as the new version.
     */
    proxyUpdate?: OrgDateCycle;

    /**
     * An array containing relationships between date cycles.
     */
    linkDateCycles: Array<{
        key: string;
        name?: string;
        year: string;
    }>;

    /**
     * The family key is used to identify the linage of the date cycle.
     * When generating new date cycles, the family key is used to determine the origin relationship between date cycles.
     * This is also used in ClientABC for query purposes.
     */
    familyKey: string;

    parameters: {
        monthEnd: number;
        monthStart: number;
        yearEnd: Timestamp;
        yearStart: Timestamp;
    };

    regenStrategy: {
        /**
         * A flag indicating that the dateCycle has regenerated.
         * if true regardless of the dateOfRegen, the dateCycle is marked as regenerated and will be ignored in the next regeneration cycle (cron-job task/cycle).
         * if false and the dateOfRegen is near, the dateCycle has not regenerated and will be regenerated in the next regeneration cycle (cron-job task/cycle).
         * Default is false.
         */
        hasRegenerated: boolean;
        /**
         * Default is 'oncePerYear' if not provided.
         *
         * oncePerYear: The dateCycle is generated once per year, normally at the end of the year but this should be when the "year end" is specified on the dateCycle.
         * multiplePerYear: The dateCycle is generated multiple times per year, normally at the end of the month/quarter but this should be when the "year end" is specified on the dateCycle. Currently, this is not supported.
         * onceOff: The dateCycle is generated once and never again. This is used for dateCycles that are not meant to regenerate. Currently, this is not supported.
         */
        frequency: 'oncePerYear' | 'multiplePerYear' | 'onceOff';
        /**
         * Default is 'atEndOfCycle' if not provided.
         *
         * fixedDate: The dateCycle is generated on a fixed date.
         * atEndOfCycle: The dateCycle is generated at the end of the cycle.
         * none: The dateCycle is not generated.
         */
        strategy: 'fixedDate' | 'atEndOfCycle' | 'none';
        /**
         * The date the dateCycle is due to regenerate.
         * The default value should be the year end date.
         *
         * Regeneration occurs 1 month before the dateOfRegen, as of now with the current cronJob. - 19-2-2024
         */
        dateOfRegen: Timestamp;
    };
}

export enum TargetType {
    'COMPANY_TARGETS' = 'COMPANY_TARGETS',
    'USER_TARGETS' = 'USER_TARGETS',
}

export interface DateSettings extends OrganizationSettings {
    dateProfileKey?: string;
    monthsOfYear?: Array<YearCycle>;
    cycle?: OrgDateCycle;
}

// ============================================================================
// POLICY MANAGEMENT SYSTEM - Enhanced Types
// ============================================================================

/**
 * Policy status throughout its lifecycle
 */
export type PolicyStatus =
    | 'draft' // Being created/edited
    | 'pending_approval' // Waiting for approvers
    | 'approved' // Approved, waiting for publish date
    | 'scheduled' // Approved, scheduled to publish on future date
    | 'published' // Active and visible to targets
    | 'expired' // Past expiry date
    | 'cancelled' // Cancelled before publication
    | 'revoked' // Revoked after publication
    | 'archived' // No longer active, kept for records
    | 'deleted'; // Soft deleted, can be reinstated

/**
 * Policy categories for organization
 */
export type PolicyCategory =
    | 'general'
    | 'hr'
    | 'it'
    | 'finance'
    | 'legal'
    | 'compliance'
    | 'safety'
    | 'operations'
    | 'custom';

/**
 * Target selection modes
 */
export type PolicyTargetMode =
    | 'all_organization' // All users in org
    | 'selected'; // Specific parts/hats/users

/**
 * Approval workflow status
 */
export type ApprovalWorkflowStatus = 'not_started' | 'in_progress' | 'approved' | 'rejected' | 'cancelled';

/**
 * Individual approver status
 *
 * `waiting` = configured but not yet reached by the chain (only the step at
 * `currentStepIndex` is `pending`, i.e. actually awaiting a decision). The
 * approval engine sets it at submit time; the configuration UI still creates
 * steps as `pending` before submission, and the display layer already renders
 * `waiting` (`org-policy-approvers-selector`).
 */
export type ApproverStatus = 'waiting' | 'pending' | 'approved' | 'rejected' | 'skipped';

/**
 * Admin override details when approval workflow is bypassed
 */
export interface PolicyAdminOverride {
    /** Whether this policy was approved via admin override */
    wasOverridden: boolean;
    /** Reason provided by admin for the override */
    overrideReason: string;
    /** Timestamp when the override occurred */
    overriddenAt: Timestamp;
    /** UID of the admin who performed the override */
    overriddenBy: string;
    /** Email of the admin who performed the override */
    overriddenByEmail?: string;
}

/**
 * Audit trail entry for tracking significant policy actions
 */
export interface PolicyAuditEntry {
    /** Type of action performed */
    action:
        | 'created'
        | 'updated'
        | 'submitted_for_approval'
        | 'approved'
        | 'rejected'
        | 'direct_approval'
        | 'admin_override_approval'
        | 'published'
        | 'revoked'
        | 'cancelled'
        | 'deleted'
        | 'reinstated'
        | 'workflow_step_approved'
        | 'workflow_step_rejected'
        /**
         * A publish transition was refused by the queue's actor gate and the
         * policy was reverted to its previous status (OE-80 / policies
         * hardening §B3). `performedBy` is the actor that attempted it.
         */
        | 'unauthorized_publish_blocked';
    /** When the action occurred */
    timestamp: Timestamp;
    /** UID of user who performed the action */
    performedBy?: string;
    /** Email of user who performed the action */
    performedByEmail?: string;
    /** Additional details specific to the action */
    details?: Record<string, unknown>;
    /** For admin override: the reason provided */
    overrideReason?: string;
    /** For admin override: whether workflow was bypassed */
    bypassedWorkflow?: boolean;
    /** Previous status before the action */
    previousStatus?: PolicyStatus;
}

/**
 * Enhanced OrgPolicy interface with full lifecycle support
 */
export interface OrgPolicy extends BaseDocumentFields {
    // === Basic Information ===
    /** Policy name/title */
    name: string;
    /** Short description of the policy */
    description?: string;
    /** Policy category for filtering/grouping */
    category: PolicyCategory;
    /** Custom category name (when category is 'custom') */
    customCategory?: string;
    /** Current version number */
    version: number;
    /** Main policy content (rich text/HTML) */
    policyWriting: string;

    // === Status & Lifecycle ===
    /** Current lifecycle status */
    status: PolicyStatus;
    /** Is the policy published and visible */
    isPublished?: boolean;
    /** Is the policy archived */
    isArchived?: boolean;

    // === Dates ===
    /** When the policy becomes effective */
    effectiveDate?: Timestamp;
    /** When the policy should be published (for scheduled publishing) */
    publishDate?: Timestamp;
    /** When the policy was actually published */
    publishedAt?: Timestamp;
    /**
     * Who asked for the publish. The backend publish path stamps the doc's
     * `modifiedBy` with its own identity (so the queue's publish gate can
     * recognise its authorized write), and keeps the human here.
     */
    publishedBy?: string;
    /** When the policy expires (optional) */
    expiryDate?: Timestamp;
    /** Deadline for users to acknowledge */
    acknowledgmentDeadline?: Timestamp;

    // === Targets ===
    /** Who should receive this policy */
    targets?: PolicyTargets;

    // === Acknowledgment ===
    /** Does this policy require acknowledgment */
    requiresAcknowledgment?: boolean;
    /** Is acknowledgment mandatory (cannot be dismissed) */
    isMandatory?: boolean;
    /** Summary stats for quick display (updated by trigger) */
    acknowledgmentStats?: PolicyAcknowledgmentStats;
    /** Per-policy reminder settings (overrides organization defaults) */
    reminderSettings?: PolicyReminderSettings;

    // === Approval Workflow ===
    /** Sequential approval workflow configuration */
    approvalWorkflow?: PolicyApprovalWorkflow;

    // === Revisions ===
    /** History of policy versions */
    revisions: PolicyRevision[];

    // === Cancel (pre-publication) ===
    /** When the policy was cancelled */
    cancelledAt?: Timestamp;
    /** Who cancelled the policy */
    cancelledBy?: string;
    /** Reason for cancellation */
    cancellationReason?: string;

    // === Revoke (post-publication) ===
    /** When the policy was revoked */
    revokedAt?: Timestamp;
    /** Who revoked the policy */
    revokedBy?: string;
    /** Reason for revocation */
    revocationReason?: string;
    /** When the revocation takes effect */
    revocationEffectiveDate?: Timestamp;

    // === Delete & Reinstate ===
    /** When the policy was soft-deleted */
    deletedAt?: Timestamp;
    /** When the policy was unpublished (if was published before delete/reschedule) */
    unpublishedAt?: Timestamp;
    /** Previous status before deletion (for reinstatement) */
    previousStatus?: PolicyStatus;
    /** When a deleted policy was reinstated */
    reinstatedAt?: Timestamp;

    // === Admin Override ===
    /** Admin override details when approval workflow was bypassed */
    adminOverride?: PolicyAdminOverride;

    // === Audit Trail ===
    /** Audit trail of significant policy actions */
    auditTrail?: PolicyAuditEntry[];

    // === Approval ===
    /** When the policy was approved */
    approvedAt?: Timestamp;
    /** Who approved the policy */
    approvedBy?: string;

    // === Audit ===
    /** Author who created the policy */
    authorUid?: string;
    /** Author display name */
    authorName?: string;
}

/**
 * Policy revision history entry - stores full snapshot for comparison
 */
export interface PolicyRevision {
    /** Version number of this revision */
    version: number;
    /** When the revision was created */
    date: Date | Timestamp;
    /** Policy name at this version */
    name: string;
    /** Policy content at this version */
    policyWriting: string;
    /** Who created this revision */
    writtenBy: string;
    /** Notes about what changed */
    changeNotes?: string;

    // === Full Snapshot Fields (for detailed comparison) ===
    /** Description at this version */
    description?: string;
    /** Category at this version */
    category?: PolicyCategory;
    /** Custom category at this version */
    customCategory?: string;
    /** Status at this version */
    status?: PolicyStatus;

    // === Dates Snapshot ===
    /** Effective date at this version */
    effectiveDate?: Timestamp | Date;
    /** Publish date at this version */
    publishDate?: Timestamp | Date;
    /** Expiry date at this version */
    expiryDate?: Timestamp | Date;
    /** Acknowledgment deadline at this version */
    acknowledgmentDeadline?: Timestamp | Date;

    // === Targets Snapshot ===
    /** Target configuration at this version */
    targets?: PolicyTargetsSnapshot;

    // === Approval Workflow Snapshot ===
    /** Approval workflow at this version */
    approvalWorkflow?: PolicyApprovalWorkflowSnapshot;

    // === Settings Snapshot ===
    /** Requires acknowledgment at this version */
    requiresAcknowledgment?: boolean;
    /** Is mandatory at this version */
    isMandatory?: boolean;
}

/**
 * Targets snapshot for revision history - includes full target data for restore
 */
export interface PolicyTargetsSnapshot {
    /** Selection mode */
    mode?: PolicyTargetMode;

    // === Full target data (for restore capability) ===
    /** Selected organizational parts - full data for restoration */
    parts?: PolicyPartTarget[];
    /** Directly selected users - full data for restoration */
    directUsers?: PolicyUserTarget[];
    /** Directly selected hats - full data for restoration */
    directHats?: PolicyHatTarget[];

    // === Summary counts (for quick display without loading full arrays) ===
    /** Part names for display */
    partNames?: string[];
    /** Direct user names for display */
    directUserNames?: string[];
    /** Direct hat names for display */
    directHatNames?: string[];
    /** Total count of parts */
    partCount?: number;
    /** Total count of direct users */
    directUserCount?: number;
    /** Total count of direct hats */
    directHatCount?: number;
}

/**
 * Simplified approval workflow snapshot for revision history
 */
export interface PolicyApprovalWorkflowSnapshot {
    /** Approver names in order */
    approverNames?: string[];
    /** Number of approval steps */
    stepCount?: number;
    /** Current status of workflow */
    workflowStatus?: ApprovalWorkflowStatus;
}

/**
 * Policy target configuration
 */
export interface PolicyTargets {
    /** Selection mode */
    mode: PolicyTargetMode;

    /** Selected organizational parts (divisions, departments, etc.) */
    parts?: PolicyPartTarget[];

    /** Directly selected users */
    directUsers?: PolicyUserTarget[];

    /** Directly selected hats */
    directHats?: PolicyHatTarget[];

    // === Resolved at publish time (by cron job) ===
    /**
     * Resolved hat keys - populated when policy is published.
     * Used for querying: "get policies for hat X" → WHERE resolvedHatKeys contains X
     * This is the "query model" - policies store which hats they target, not vice versa.
     */
    resolvedHatKeys?: string[];

    /**
     * Resolved/affected user UIDs - populated when policy is published.
     * Denormalized list of all user UIDs who need to acknowledge this policy.
     * Used for creating acknowledgment records and querying user's pending policies.
     */
    affectedUserUids?: string[];

    /** Total resolved user count */
    resolvedUserCount?: number;
    /** Total resolved hat count */
    resolvedHatCount?: number;
    /** When targets were last resolved */
    resolvedAt?: Timestamp;
}

/**
 * Part (position) target with cascade option
 */
export interface PolicyPartTarget {
    /** Part key */
    key: string;
    /** Part name for display */
    name: string;
    /** Part type (division, department, section, unit, post) */
    partType?: string;
    /** Include all children of this part */
    includeChildren: boolean;
}

/**
 * Direct user target
 */
export interface PolicyUserTarget {
    /** User UID */
    uid: string;
    /** User display name */
    name: string;
    /** User email */
    email?: string;
}

/**
 * Direct hat target
 */
export interface PolicyHatTarget {
    /** Hat key */
    key: string;
    /** Hat name for display */
    name: string;
}

/**
 * Acknowledgment statistics (summary on policy document)
 */
export interface PolicyAcknowledgmentStats {
    /** Total users required to acknowledge */
    totalRequired: number;
    /** Number of users who have acknowledged */
    totalAcknowledged: number;
    /** Completion percentage (0-100) */
    completionPercentage: number;
    /** Last time stats were updated */
    lastUpdated?: Timestamp;
    /** When last acknowledgment was received */
    lastAcknowledgedAt?: Timestamp;
    /** Last user to acknowledge (for activity feed) */
    lastAcknowledgedBy?: {
        uid: string;
        name: string;
    };
}

/**
 * Sequential approval workflow
 */
export interface PolicyApprovalWorkflow {
    /** Ordered list of approval steps */
    steps: PolicyApprovalStep[];
    /** Current step index (-1 if not started, >= steps.length if complete) */
    currentStepIndex: number;
    /** Overall workflow status */
    workflowStatus: ApprovalWorkflowStatus;
    /** Can the policy be resubmitted after rejection */
    canResubmit: boolean;
    /** When the workflow was started */
    startedAt?: Timestamp;
    /** When the workflow was completed */
    completedAt?: Timestamp;
}

/**
 * Single approval step in the workflow
 */
export interface PolicyApprovalStep {
    /** Step order (0-based) */
    order: number;
    /** Step name/label */
    name: string;
    /** Approver for this step */
    approver: PolicyApprover;
    /** Deadline for this approval (optional) */
    deadline?: Timestamp;
    /** Deadline in days from previous step */
    deadlineDays?: number;
    /** Current status of this step */
    status: ApproverStatus;
    /** When this step was actioned */
    actionedAt?: Timestamp;
    /** When the approver's decision was recorded (audit precision, set by the approval engine) */
    decidedAt?: Timestamp;
    /** Comment from approver */
    comment?: string;
    /** Escalation count (how many times escalated) */
    escalationCount?: number;
    /** When last escalation was sent */
    lastEscalationAt?: Timestamp;
    /**
     * Set once the deadline cron has escalated this step (design §2d). It is the
     * idempotency guard — an overdue step is escalated exactly once, not every
     * five minutes — so it is deliberately a flag and not derived from
     * `escalationCount`, which stays free for a future repeat-escalation policy.
     */
    escalationSent?: boolean;
}

/**
 * Approver reference
 */
export interface PolicyApprover {
    /** Approver type */
    type: 'user' | 'hat';
    /** User UID (if type is 'user') */
    uid?: string;
    /** Hat key (if type is 'hat') */
    hatKey?: string;
    /** Display name */
    name: string;
    /** Email for notifications */
    email?: string;
}

// ============================================================================
// POLICY ACKNOWLEDGMENT - Separate Collection
// ============================================================================

/**
 * Individual acknowledgment record
 * Collection: organizations/{orgId}/policyAcknowledgments/{acknowledgmentId}
 */
export interface PolicyAcknowledgment {
    /** Unique acknowledgment key */
    key: string;
    /** Policy that was acknowledged */
    policyKey: string;
    /** Policy name (for display without join) */
    policyName: string;
    /** Policy version that was acknowledged */
    policyVersion: number;
    /** User who acknowledged */
    uid: string;
    /** User display name */
    userName: string;
    /** User email */
    userEmail?: string;
    /** Organization key */
    orgKey: string;

    // === Acknowledgment Steps ===
    /** User confirmed they read the policy */
    hasRead: boolean;
    /** When they confirmed reading */
    readAt?: Timestamp;
    /** User confirmed they understand the policy */
    hasUnderstood: boolean;
    /** When they confirmed understanding */
    understoodAt?: Timestamp;
    /** User agreed to comply with the policy */
    hasAgreed: boolean;
    /** When they agreed */
    agreedAt?: Timestamp;

    // === Completion ===
    /** All three steps completed */
    isComplete: boolean;
    /** When fully acknowledged */
    completedAt?: Timestamp;

    // === Re-acknowledgment (when policy version changes) ===
    /** User needs to re-acknowledge due to policy version change */
    requiresReacknowledgment?: boolean;
    /** The new policy version that requires re-acknowledgment */
    newPolicyVersion?: number;
    /** When the re-acknowledgment requirement was set */
    reacknowledgmentRequiredAt?: Timestamp;
    /** History of previous acknowledgments */
    acknowledgmentHistory?: Array<{
        policyVersion: number;
        completedAt: Timestamp;
    }>;

    // === Context ===
    /** How the user received this policy (via hat, direct, part) */
    receivedVia?: 'hat' | 'direct_user' | 'part';
    /** Key of the hat/part if applicable */
    receivedViaKey?: string;
    /** Name of the hat/part if applicable */
    receivedViaName?: string;

    // === Reminder Tracking ===
    /** When the last reminder was sent */
    lastReminderSentAt?: Timestamp;
    /** Total number of reminders sent */
    reminderCount?: number;
    /** Milliseconds before deadline when last reminder was sent (for matching thresholds) */
    lastReminderMsBefore?: number;
    /** Number of overdue reminders sent (after deadline passed) */
    overdueReminderCount?: number;
    /** When last overdue reminder was sent */
    lastOverdueReminderAt?: Timestamp;

    // === Onboarding Context ===
    /** True if the policy's acknowledgment deadline had already passed when this user was assigned */
    deadlinePassedOnAssignment?: boolean;

    // === Timestamps ===
    /** When the acknowledgment record was created */
    createdAt: Timestamp;
    /** When the record was last updated */
    updatedAt: Timestamp;
}

// ============================================================================
// POLICY AUDIT LOG
// ============================================================================

/**
 * Policy audit log entry
 * Collection: organizations/{orgId}/policyAuditLog/{logId}
 */
export interface PolicyAuditLogEntry {
    /** Unique log entry key */
    key: string;
    /** Policy key */
    policyKey: string;
    /** Policy name at time of action */
    policyName: string;
    /** Policy version at time of action */
    policyVersion: number;
    /** Organization key */
    orgKey: string;

    // === Action Details ===
    /** Type of action */
    action: PolicyAuditAction;
    /** User who performed the action */
    actorUid: string;
    /** Actor display name */
    actorName: string;
    /** When the action occurred */
    timestamp: Timestamp;

    // === Context ===
    /** Previous status (for status changes) */
    previousStatus?: PolicyStatus;
    /** New status (for status changes) */
    newStatus?: PolicyStatus;
    /** Additional details */
    details?: Record<string, any>;
    /** Comment or reason */
    comment?: string;
}

/**
 * Types of auditable policy actions
 */
export type PolicyAuditAction =
    | 'created'
    | 'updated'
    | 'submitted_for_approval'
    | 'approval_step_completed'
    | 'approval_step_rejected'
    | 'fully_approved'
    | 'published'
    | 'acknowledged'
    | 'cancelled'
    | 'revoked'
    | 'archived'
    | 'version_created'
    | 'targets_updated'
    | 'reminder_sent'
    | 'escalation_sent';

// ============================================================================
// LEGACY INTERFACE (keeping for backwards compatibility)
// ============================================================================

/**
 * @deprecated Use OrgPolicy with full fields instead
 * Legacy policy interface for migration reference
 */
export interface LegacyOrgPolicy {
    dateCreated?: Timestamp;
    key: string;
    lastModifiedBy?: string;
    lastModifiedDate?: Timestamp;
    name: string;
    policyWriting: string;
    revisions?: Array<{
        date: Timestamp;
        name: string;
        policyWriting: string;
        version: number;
        writtenBy: string;
    }>;
    version: number;
}

// ============================================================================
// HAT ASSIGNMENT APPROVAL WORKFLOW
// ============================================================================

/**
 * Approval record that groups pending hat entries together with position context.
 * Stored in `organizations/{orgId}/pendingApprovals/{approvalId}`.
 *
 * When an admin assigns a user to a position (with approval required), one record
 * is created capturing all selected hats. The approver can alter the hat selection
 * before approving.
 */
export interface PendingPositionApproval extends BaseDocumentFields {
    /** User UID being assigned. */
    uid: UID;
    /** Position the user was dropped on (empty string for pure special-rights assignment). */
    positionKey: string;
    /** Denormalized position name for display. */
    positionName?: string;
    /** All hats selected during assignment (position hats + special rights). */
    hatKeys: string[];
    /** Subset of hatKeys that are positionless special-rights hats (for display clarity). */
    specialRightsHatKeys?: string[];
    /** Organization key this approval belongs to. */
    orgId: string;
    /** UID of the admin who requested the assignment. */
    requestedBy: UID;
    /** Timestamp of the assignment request. */
    requestedOn: NewgenusTimestamp;
    /** Current status of the approval. */
    status: 'pending' | 'approved' | 'rejected';
    /**
     * Where the assignment originated.
     * - `position-assignment`: admin drag-dropped a user onto a position on the org-board
     * - `hat-edit`: admin assigned via the hat-edit dialog
     * - `wizard-prestage`: created by the Create-Organization wizard's staff stage
     *   (Phase B). Used by `addUserToOrganization` to dedupe on acceptance so the
     *   accept flow doesn't create a second `pendingApprovals` doc for the same
     *   (uid, hatKeys) tuple.
     */
    source?: 'position-assignment' | 'hat-edit' | 'wizard-prestage';
    /**
     * For `source === 'wizard-prestage'` only: back-link to the invitation that
     * produced this pre-stage record. Lets `rollbackWizardPrestageOnInvitationDeath`
     * find and archive the right approval when the invitation is declined/revoked/expired.
     */
    sourceInvitationKey?: string;
    /** Hats the approver actually approved (may differ from original hatKeys). */
    approvedHatKeys?: string[];
    /** UID of the admin who approved the assignment. */
    approvedBy?: UID;
    /** Timestamp of the approval. */
    approvedOn?: NewgenusTimestamp;
    /** UID of the admin who rejected the assignment. */
    rejectedBy?: UID;
    /** Timestamp of the rejection. */
    rejectedOn?: NewgenusTimestamp;
    /** Optional reason for rejection. */
    rejectionReason?: string;
}
